/**
 * Local Database Store
 *
 * Provides shared state and utility functions for the local database service.
 */

import type {
  LocalLedger,
  LocalAccount,
  LocalJournalEntry,
  LocalEntity,
  LocalFiscalPeriod,
  ElectronWindow,
} from './types'
import type { AccountType } from '../../constants/labels'

// ============================================================================
// Environment Detection
// ============================================================================

export const isElectron = typeof window !== 'undefined' && 'electronAPI' in window

// Helper to safely cast window to ElectronWindow
export function getElectronWindow(): ElectronWindow {
  return window as unknown as ElectronWindow
}

// ============================================================================
// In-Memory Store
// ============================================================================

export interface LocalStore {
  ledgers: Map<string, LocalLedger>
  accounts: Map<string, LocalAccount>
  journalEntries: Map<string, LocalJournalEntry>
  entities: Map<string, LocalEntity>
  fiscalPeriods: Map<string, LocalFiscalPeriod>
  sequences: Map<string, number>
  initialized: boolean
  currentOrgId: string | null
  defaultLedgerId: string | null
}

export const localStore: LocalStore = {
  ledgers: new Map(),
  accounts: new Map(),
  journalEntries: new Map(),
  entities: new Map(),
  fiscalPeriods: new Map(),
  sequences: new Map(),
  initialized: false,
  currentOrgId: null,
  defaultLedgerId: null,
}

// ============================================================================
// LocalStorage Persistence (Web Development)
// ============================================================================

const STORAGE_KEYS = {
  ledgers: 'accounting_ledgers',
  accounts: 'accounting_accounts',
  journalEntries: 'accounting_journal_entries',
  entities: 'accounting_entities',
  fiscalPeriods: 'accounting_fiscal_periods',
  sequences: 'accounting_sequences',
  defaultLedgerId: 'accounting_default_ledger_id',
}

export function saveToLocalStorage(): void {
  /* v8 ignore next */
  if (isElectron) {return}
  try {
    localStorage.setItem(STORAGE_KEYS.ledgers, JSON.stringify(Array.from(localStore.ledgers.entries())))
    localStorage.setItem(STORAGE_KEYS.accounts, JSON.stringify(Array.from(localStore.accounts.entries())))
    localStorage.setItem(STORAGE_KEYS.journalEntries, JSON.stringify(Array.from(localStore.journalEntries.entries())))
    localStorage.setItem(STORAGE_KEYS.entities, JSON.stringify(Array.from(localStore.entities.entries())))
    localStorage.setItem(STORAGE_KEYS.fiscalPeriods, JSON.stringify(Array.from(localStore.fiscalPeriods.entries())))
    localStorage.setItem(STORAGE_KEYS.sequences, JSON.stringify(Array.from(localStore.sequences.entries())))
    if (localStore.defaultLedgerId) {
      localStorage.setItem(STORAGE_KEYS.defaultLedgerId, localStore.defaultLedgerId)
    }
  /* v8 ignore next 3 */
  } catch {
    // silently ignore localStorage save failure
  }
}

function parseStoredMap<T>(key: string): Map<string, T> | null {
  const data = localStorage.getItem(key)
  if (!data) { return null }
  return new Map(JSON.parse(data) as [string, T][])
}

function restoreMapIfPresent<T>(key: string, target: Map<string, T>): void {
  const parsed = parseStoredMap<T>(key)
  if (parsed) { parsed.forEach((v, k) => target.set(k, v)) }
}

export function loadFromLocalStorage(): boolean {
  /* v8 ignore next */
  if (isElectron) { return false }
  try {
    const ledgersData = localStorage.getItem(STORAGE_KEYS.ledgers)
    if (!ledgersData) { return false }

    const ledgers = JSON.parse(ledgersData) as [string, LocalLedger][]
    if (ledgers.length === 0) { return false }

    localStore.ledgers = new Map(ledgers)
    restoreMapIfPresent<LocalAccount>(STORAGE_KEYS.accounts, localStore.accounts)
    restoreMapIfPresent<LocalJournalEntry>(STORAGE_KEYS.journalEntries, localStore.journalEntries)
    restoreMapIfPresent<LocalEntity>(STORAGE_KEYS.entities, localStore.entities)
    restoreMapIfPresent<LocalFiscalPeriod>(STORAGE_KEYS.fiscalPeriods, localStore.fiscalPeriods)
    restoreMapIfPresent<number>(STORAGE_KEYS.sequences, localStore.sequences)

    const defaultLedgerId = localStorage.getItem(STORAGE_KEYS.defaultLedgerId)
    if (defaultLedgerId) { localStore.defaultLedgerId = defaultLedgerId }

    return true
  } catch {
    // silently ignore localStorage load failure
    return false
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

export function getNormalBalance(type: AccountType): 'NORMAL_BALANCE_DEBIT' | 'NORMAL_BALANCE_CREDIT' {
  switch (type) {
    case 'ACCOUNT_TYPE_ASSET':
    case 'ACCOUNT_TYPE_EXPENSE':
      return 'NORMAL_BALANCE_DEBIT'
    case 'ACCOUNT_TYPE_LIABILITY':
    case 'ACCOUNT_TYPE_EQUITY':
    case 'ACCOUNT_TYPE_REVENUE':
      return 'NORMAL_BALANCE_CREDIT'
    default:
      return 'NORMAL_BALANCE_DEBIT'
  }
}

export function getNextSequence(orgId: string, type: string): number {
  const key = `${orgId}:${type}`
  const current = localStore.sequences.get(key) ?? 0
  const next = current + 1
  localStore.sequences.set(key, next)
  return next
}

/**
 * Calculate account balances from posted journal entries
 */
export function calculateAccountBalances(
  orgId: string,
  ledgerId: string | null,
  asOfDate?: string
): Map<string, { debit: number; credit: number }> {
  const balances = new Map<string, { debit: number; credit: number }>()

  const allEntries = Array.from(localStore.journalEntries.values())

  const entries = allEntries.filter(e => {
    const isMatchingOrg = e.orgId === orgId
    const isMatchingLedger = !ledgerId || e.ledgerId === ledgerId
    const isPosted = e.status === 'posted'
    const isWithinDate = !asOfDate || e.entryDate <= asOfDate

    return isMatchingOrg && isMatchingLedger && isPosted && isWithinDate
  })

  for (const entry of entries) {
    for (const line of entry.lines) {
      const existing = balances.get(line.accountId) ?? { debit: 0, credit: 0 }
      existing.debit += line.debitAmount
      existing.credit += line.creditAmount
      balances.set(line.accountId, existing)
    }
  }

  return balances
}
